/**
 * Comment une plage de tonalité s'écrit pour le joueur.
 *
 * Deux lignes : la tonalité, et ce qu'elle est à celle du morceau. C'est le
 * second mot qui enseigne — « relatif » dit que l'armure ne bouge pas et que
 * seule la tonique change — et c'est pour lui que la plage porte sa relation
 * plutôt que ses deux noms. Écrit dans la notation des notes du joueur, lue au
 * moment de l'écriture : le rouleau redessine à chaque image, et une étiquette
 * cuite à la construction survivrait à un changement de notation.
 */

import type { NoteNotation } from "@/lib/music/notation";
import { getNotationStore } from "@/lib/music/notation-store";
import { tonalityShortLabel, type Tonality } from "@/lib/music/tonality";
import type { KeySpan } from "./key-spans";

export type KeyLabel = {
  /** La tonalité, abrégée : « Ré m ». */
  readonly key: string;
  /** Ce qu'elle est à celle du morceau : « relatif ». */
  readonly relation: string;
};

/**
 * Abrégée, et sur une seule ligne.
 *
 * Le rouleau est la surface où les notes passent : une étiquette y prend la
 * place de ce qu'on est venu lire. « Ré m » et non « Ré mineur », un mot et
 * non une phrase — la tonalité du morceau est déjà écrite dans la barre
 * d'outils, et le mot « relatif » suffit à dire le lien. La partition, elle,
 * a de la place sous sa règle et écrit les deux en toutes lettres.
 */
export function keyLabelOf(span: KeySpan): KeyLabel {
  return keyLabelIn(span, getNotationStore().noteNotation.value);
}

function keyLabelIn(span: KeySpan, notation: NoteNotation): KeyLabel {
  const here: Tonality = { tonic: span.tonic, mode: span.mode };
  const key = tonalityShortLabel(here, notation);

  switch (span.relation) {
    case "home":
      return { key, relation: "retour" };
    case "relative":
      return { key, relation: "relatif" };
    case "parallel":
      return { key, relation: "homonyme" };
    default:
      return { key, relation: "modulation" };
  }
}

export type KeyLabeler = (span: KeySpan) => KeyLabel;

/**
 * Une fonction par notation, gardée, et c'est son identité qui porte le
 * message.
 *
 * Le rouleau ne redessine sa marge que si quelque chose a changé dans ses
 * props, et il compare le nomenclateur par identité. Un `keyLabelOf` unique
 * qui lit la notation à l'intérieur reste donc le même objet d'une image à
 * l'autre : passer de « Ré m » à « Dm » ne changeait rien de comparable et
 * l'étiquette restait à l'ancienne écriture jusqu'à ce que la lecture reprenne.
 * Deux fonctions gardées, une par notation, font du changement de réglage un
 * changement de props.
 */
const LABELERS: Record<NoteNotation, KeyLabeler> = {
  english: (span) => keyLabelIn(span, "english"),
  latin: (span) => keyLabelIn(span, "latin"),
};

/** Le nomenclateur de la notation courante, stable tant qu'elle ne change pas. */
export function currentKeyLabeler(): KeyLabeler {
  return LABELERS[getNotationStore().noteNotation.value];
}
